import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';

export function checkPasswordStrength(password: string) {
  const hasMinLength = password.length >= 8;
  const hasNumber = /[0-9]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const strength = [hasMinLength, hasNumber, hasUpperCase, hasLowerCase, hasSpecialChar].filter(Boolean).length;
  return {
    hasMinLength,
    hasNumber,
    hasUpperCase,
    hasLowerCase,
    hasSpecialChar,
    strength,
    isValid: strength >= 4
  };
}

const initialStrength = {
  hasMinLength: false,
  hasNumber: false,
  hasUpperCase: false,
  hasLowerCase: false,
  hasSpecialChar: false,
  strength: 0,
  isValid: false
};

export function useResetPasswordConfirmPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [token, setToken] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(initialStrength);
  const [isValidatingToken, setIsValidatingToken] = useState(true);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setError('Invalid reset link. Please request a new password reset.');
      setIsValidatingToken(false);
      return;
    }
    setToken(tokenFromUrl);
    setIsValidatingToken(false);
  }, [searchParams]);

  useEffect(() => {
    if (formData.password) {
      setPasswordStrength(checkPasswordStrength(formData.password));
    } else {
      setPasswordStrength(initialStrength);
    }
  }, [formData.password]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!passwordStrength.isValid) {
      setError('Please ensure your password meets all the requirements');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, formData.password);
      track(AnalyticsEvents.PASSWORD_RESET_COMPLETE);
      setResetSuccess(true);
      setMessage('Password has been reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to reset password. Please try again.';
      setError(errorMessage);
      if (errorMessage.includes('Invalid') || errorMessage.includes('expired') || errorMessage.includes('used')) {
        setToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    message,
    error,
    loading,
    resetSuccess,
    token,
    passwordStrength,
    isValidatingToken,
    handleChange,
    handleSubmit,
  };
}
